import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowUpRight, Check, CheckCircle2, Sparkles, ShieldCheck, Truck,
  Package, Upload, FileText, X,
} from "lucide-react";
import { API } from "@/lib/auth";

const MAX_MB = 10;
const BENEFITS = [
  { label: "15% Trade Discount on NøoksPro Hair Extensions", Icon: Sparkles },
  { label: "Premium Remy Hair", Icon: Package },
  { label: "Access to New Product Launches", Icon: ArrowUpRight },
  { label: "Fast UK Delivery", Icon: Truck },
  { label: "Dedicated Trade Support", Icon: ShieldCheck },
  { label: "No Minimum Order Value", Icon: Check },
  { label: "Automatic Trade Pricing Once Approved", Icon: CheckCircle2 },
];

export default function TradeAccount() {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    website: "",
    instagram: "",
    salon_address: "",
    years_experience: "",
    based: "salon",
    insurance_provider: "",
  });
  const [confirms, setConfirms] = useState({
    confirm_accurate: false,
    confirm_manual_review: false,
    confirm_scope: false,
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    const ok = /\.(pdf|jpe?g|png)$/i.test(f.name) || /^(application\/pdf|image\/(jpe?g|png))$/i.test(f.type);
    if (!ok) return toast.error("Please upload a PDF, JPG or PNG certificate.");
    if (f.size > MAX_MB * 1024 * 1024) return toast.error(`File too large (max ${MAX_MB} MB).`);
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Please upload your qualification certificate.");
    if (!confirms.confirm_accurate || !confirms.confirm_manual_review || !confirms.confirm_scope) {
      return toast.error("Please tick all three confirmation boxes.");
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("certificate", file);
      Object.entries(form).forEach(([k, v]) => {
        if (v?.toString().trim()) fd.append(k, v.trim());
      });
      Object.entries(confirms).forEach(([k, v]) => fd.append(k, v ? "true" : "false"));
      await axios.post(`${API}/trade/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSubmitted(true);
      toast.success("Application received");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="page-trade-success" className="pt-40 pb-24 min-h-[70vh]">
        <div className="mx-auto max-w-[720px] px-6 text-center">
          <div className="w-20 h-20 rounded-full bg-blush mx-auto flex items-center justify-center">
            <CheckCircle2 size={36} className="text-[hsl(var(--burgundy))]" />
          </div>
          <div className="overline mt-8">Application received</div>
          <h1 className="font-display text-5xl mt-3 tracking-tight text-balance">Thank you — we've got it.</h1>
          <p className="mt-4 text-[hsl(var(--onyx)/0.65)] leading-relaxed">
            We've received your qualification certificate. Our team manually reviews every trade application — you'll receive an email once your Trade Account has been approved.
          </p>
          <Link to="/shop" className="btn-pill btn-primary mt-10 inline-flex" data-testid="trade-success-shop">
            Continue browsing <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-trade" className="pt-32 pb-24">
      {/* Hero */}
      <div className="mx-auto max-w-[1200px] px-6 sm:px-10">
        <div className="grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline">NøoksPro Trade Partner</div>
            <h1 className="font-display text-5xl sm:text-6xl mt-3 tracking-tight text-balance">
              Become a NøoksPro <span className="italic font-light">Trade Partner.</span>
            </h1>
            <p className="mt-4 text-lg text-[hsl(var(--onyx)/0.7)] leading-relaxed">
              Professional hair extensions for professional stylists.
            </p>
            <p className="mt-3 text-[hsl(var(--onyx)/0.65)] leading-relaxed max-w-xl">
              Upload your qualification certificate to apply for a NøoksPro Trade Account and receive
              <strong> 15% OFF</strong> all NøoksPro hair extension products.
            </p>
            <p className="mt-2 text-xs text-[hsl(var(--onyx)/0.5)] max-w-xl leading-relaxed">
              Trade discounts do not apply to education courses, masterclasses or training.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] bg-white border border-[hsl(var(--onyx)/0.08)] p-6 shadow-[0_20px_60px_-30px_rgba(20,10,15,0.15)]">
              <div className="overline mb-4">Trade benefits</div>
              <ul className="space-y-3">
                {BENEFITS.map(({ label, Icon }) => (
                  <li key={label} className="flex items-start gap-3">
                    <div className="shrink-0 w-8 h-8 rounded-full bg-blush flex items-center justify-center">
                      <Icon size={14} className="text-[hsl(var(--burgundy))]" />
                    </div>
                    <span className="text-sm text-[hsl(var(--onyx)/0.8)] leading-relaxed pt-1.5">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="trade-form"
          className="mt-16 rounded-[2rem] bg-white border border-[hsl(var(--onyx)/0.08)] p-8 sm:p-12 shadow-[0_20px_60px_-30px_rgba(20,10,15,0.15)]"
        >
          <div className="overline">Application</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Register your trade account.</h2>

          {/* Personal */}
          <div className="mt-8">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--burgundy))] mb-4">Personal Information</div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Full name *" testid="trade-full-name" value={form.full_name} onChange={(v)=>setForm({...form, full_name:v})} required />
              <Field label="Business name *" testid="trade-business-name" value={form.business_name} onChange={(v)=>setForm({...form, business_name:v})} required />
              <Field label="Email *" type="email" testid="trade-email" value={form.email} onChange={(v)=>setForm({...form, email:v})} required />
              <Field label="Phone *" type="tel" testid="trade-phone" value={form.phone} onChange={(v)=>setForm({...form, phone:v})} required />
              <Field label="Website (optional)" type="url" testid="trade-website" value={form.website} onChange={(v)=>setForm({...form, website:v})} placeholder="https://..." />
              <Field label="Instagram handle" testid="trade-instagram" value={form.instagram} onChange={(v)=>setForm({...form, instagram:v})} placeholder="@yourhandle" />
              <div className="sm:col-span-2">
                <Field label="Salon address *" testid="trade-salon-address" value={form.salon_address} onChange={(v)=>setForm({...form, salon_address:v})} required placeholder="Street, city, postcode" />
              </div>
            </div>
          </div>

          {/* Professional */}
          <div className="mt-10">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--burgundy))] mb-4">Professional Information</div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Years in the hair industry *" testid="trade-years" value={form.years_experience} onChange={(v)=>setForm({...form, years_experience:v})} required placeholder="e.g. 5+ years" />
              <div>
                <label className="overline">Based *</label>
                <select
                  value={form.based}
                  onChange={(e)=>setForm({...form, based:e.target.value})}
                  data-testid="trade-based"
                  className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] bg-white px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
                >
                  <option value="salon">Salon based</option>
                  <option value="mobile">Mobile</option>
                  <option value="both">Both salon & mobile</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <Field label="Insurance provider (optional)" testid="trade-insurance" value={form.insurance_provider} onChange={(v)=>setForm({...form, insurance_provider:v})} />
              </div>
            </div>
          </div>

          {/* Certificate upload */}
          <div className="mt-10">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--burgundy))] mb-4">Qualification</div>
            <label className="overline">Upload your Hair Extension Qualification Certificate *</label>
            <div className="mt-2 rounded-3xl border-2 border-dashed border-[hsl(var(--onyx)/0.15)] hover:border-[hsl(var(--burgundy))] transition-colors bg-blush/40 p-6">
              {file ? (
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white border border-[hsl(var(--onyx)/0.08)] flex items-center justify-center shrink-0">
                    <FileText size={26} className="text-[hsl(var(--burgundy))]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{file.name}</div>
                    <div className="text-xs text-[hsl(var(--onyx)/0.55)] mt-1">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  </div>
                  <button
                    type="button"
                    data-testid="trade-clear-cert"
                    onClick={() => { setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                    className="w-9 h-9 rounded-full hover:bg-[hsl(var(--onyx)/0.06)] flex items-center justify-center"
                  >
                    <X size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="trade-pick-cert"
                  onClick={() => inputRef.current?.click()}
                  className="w-full flex flex-col items-center gap-2 py-6"
                >
                  <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center border border-[hsl(var(--onyx)/0.08)]">
                    <Upload size={20} className="text-[hsl(var(--burgundy))]" />
                  </div>
                  <div className="font-display text-lg mt-2">Add your certificate</div>
                  <div className="text-xs text-[hsl(var(--onyx)/0.55)]">
                    PDF, JPG or PNG · up to {MAX_MB} MB
                  </div>
                  <div className="text-[11px] text-[hsl(var(--onyx)/0.5)] mt-2 max-w-xs text-center">
                    Accepted: Accredited Extension Courses · Recognised Industry Certificates
                  </div>
                </button>
              )}
              <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*" onChange={(e)=>handleFile(e.target.files?.[0])} className="hidden" data-testid="trade-cert-input" />
            </div>
          </div>

          {/* Terms */}
          <div className="mt-10 space-y-3">
            <Confirm
              value={confirms.confirm_accurate}
              onChange={(v)=>setConfirms({...confirms, confirm_accurate:v})}
              label="I confirm that all information supplied is accurate."
              testid="trade-confirm-accurate"
            />
            <Confirm
              value={confirms.confirm_manual_review}
              onChange={(v)=>setConfirms({...confirms, confirm_manual_review:v})}
              label="I understand trade accounts are manually reviewed before approval."
              testid="trade-confirm-review"
            />
            <Confirm
              value={confirms.confirm_scope}
              onChange={(v)=>setConfirms({...confirms, confirm_scope:v})}
              label="I understand my trade discount only applies to NøoksPro products and excludes all training courses, masterclasses, education and gift cards."
              testid="trade-confirm-scope"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="trade-submit"
            className="btn-pill btn-primary w-full mt-10 disabled:opacity-60"
          >
            {loading ? "Submitting your application…" : "Apply for Trade Account"} <ArrowUpRight size={16} />
          </button>
          <p className="mt-4 text-xs text-[hsl(var(--onyx)/0.5)] leading-relaxed">
            Applications are reviewed within 2 working days. Your certificate is kept private and only visible to our review team.
          </p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder }) {
  return (
    <div>
      <label className="overline">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e)=>onChange(e.target.value)}
        data-testid={testid}
        placeholder={placeholder}
        className="mt-2 w-full h-12 rounded-2xl border border-[hsl(var(--onyx)/0.15)] px-4 text-sm outline-none focus:border-[hsl(var(--onyx))]"
      />
    </div>
  );
}

function Confirm({ value, onChange, label, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer group">
      <span
        className={`shrink-0 mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
          value ? "border-[hsl(var(--burgundy))] bg-[hsl(var(--burgundy))]" : "border-[hsl(var(--onyx)/0.2)] group-hover:border-[hsl(var(--burgundy))]"
        }`}
      >
        {value && <Check size={12} className="text-[hsl(var(--cream))]" />}
      </span>
      <input
        type="checkbox"
        checked={value}
        onChange={(e)=>onChange(e.target.checked)}
        data-testid={testid}
        className="sr-only"
      />
      <span className="text-sm text-[hsl(var(--onyx)/0.75)] leading-relaxed">{label}</span>
    </label>
  );
}
